import type { CoveragesProps } from '@/components/life/Coverages';
import type { HeroProps } from '@/components/life/Hero';
import type { TrustedProps } from '@/components/life/Trusted';
import { sharedConfig } from '@/pageConfig/shared';
import appsImg from '@/static/life/apps.svg';
import collegeImg from '@/static/life/college.svg';
import debtImg from '@/static/life/debt.svg';
import expensesImg from '@/static/life/expenses.svg';
import homeImg from '@/static/life/home.svg';
import incomeImg from '@/static/life/income.svg';
import og from '@/static/life/og.png';
import payoutsImg from '@/static/life/payouts.svg';
import qualityImg from '@/static/life/quality.svg';
import roomLightImg from '@/static/life/room-light.svg';
import yearsImg from '@/static/life/years.svg';
import type { BasePageConfig } from '../../BasePageConfig';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Life Insurance',
  description: 'Life Insurance policies',
  image: roomLightImg.src,
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.4',
    ratingCount: '13,243',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '8',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  trusted: TrustedProps;
  coverages: CoveragesProps;
}

export const config: Config = {
  layout: {
    ...sharedConfig['en-US'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  seo: {
    title: 'Term Life Insurance offered by Lemonade',
    metaTitle: 'Term Life Insurance offered by Lemonade',
    description:
      'Term Life Insurance: Starting from $8 a month Offered by Lemonade, America’s top rated insurance company. Sign up for term life coverage today.',
    ogImage: og.src,
    jsonld: [productJsonld],
  },
  hero: {
    title: 'Term life insurance for the next generation',
    subtitle: 'The most coverage for your family, at the lowest possible price.',
  },
  trusted: {
    title: 'Trusted by 1.5 million customers',
    subtitle: 'We partnered with Banner Life to provide you with affordable and customizable coverage',
    darkBackground: true,
    data: [
      {
        id: 'payouts',
        title: 'Payouts as high as<br>$100,000,000',
        background: payoutsImg.src,
        alt: 'Life insurance payouts',
      },
      {
        id: 'applications',
        title: '~90% applications approved',
        background: appsImg.src,
        alt: 'Life insurance applications',
      },
      {
        id: 'yearsInBusiness',
        title: '180+ years in<br>business',
        background: yearsImg.src,
        alt: 'Life insurance years in business',
      },
    ],
  },
  coverages: {
    title: "Here for your loved ones,<br>when you're not around",
    subtitle: 'Term life insurance helps cover the financial burden of your loss for the people who matter most',
    data: [
      {
        id: 'incomeContinuity',
        title: 'Income continuity',
        description: `Maintain your family's ongoing financial security`,
        background: incomeImg.src,
        alt: 'Life insurance income continuity',
      },
      {
        id: 'homePayments',
        title: 'Home payments',
        description: 'Pay off your mortgage and maintain your property',
        background: homeImg.src,
        alt: 'Life insurance home payments',
      },
      {
        id: 'remainingDebt',
        title: 'Remaining debt',
        description: `Handle your loans and debts so your family doesn't have to`,
        background: debtImg.src,
        alt: 'Life insurance remaining debt',
      },
      {
        id: 'collegeTuition',
        title: 'College tuition',
        description: `Guarantee your children's education`,
        background: collegeImg.src,
        alt: 'Life insurance college tuition',
      },
      {
        id: 'finalExpenses',
        title: 'Final expenses',
        description: 'Burial, funeral services, or cremation expenses',
        background: expensesImg.src,
        alt: 'Life insurance final expenses',
      },
      {
        id: 'qualityOfLife',
        title: 'Quality of life',
        description: 'Help your loved ones maintain their current lifestyle',
        background: qualityImg.src,
        alt: 'Life insurance quality of life',
      },
    ],
  },
};
